import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Checkbox, {
    CheckboxPresentationProps,
    StyleProps as CheckboxStyleProps,
} from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import PanelMenu, { PanelMenuPresentationProps, PanelRowPresentationProps } from "@insite/mobius/PanelMenu";
import PanelRow from "@insite/mobius/PanelMenu/PanelRow";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ArrowLeftIcon from "@tCentro/Icons/ArrowLeftIcon";
import ArrowRightIcon from "@tCentro/Icons/ArrowRightIcon";
import NISArrowLeftIcon from "@tCentro/Icons/NISArrowLeftIcon";
import NISDownIcon from "@tCentro/Icons/NISDownIcon";
import NISUpIcon from "@tCentro/Icons/NISUpIcon";
import SearchIcon from "@tCentro/Icons/SearchIcon";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { FC, useState } from "react";
import { css } from "styled-components";

export interface ProductListFilterAccordionSectionStyles {
    // Accordian Section Styles
    accordionSection?: AccordionSectionPresentationProps;
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    seeAllLink?: LinkPresentationProps;
    seeLessLink?: LinkPresentationProps;
    input?: TextFieldPresentationProps;
    accordionMainContainerStyles?: InjectableCss;

    // Drawer Section Styles
    panelMenuStyles?: PanelMenuPresentationProps;
    panelrowStyles?: PanelRowPresentationProps;
    childrenStyles?: InjectableCss;
    checkboxStyles?: CheckboxPresentationProps;
    drawerStyles?: DrawerPresentationProps;
    childrenPanelrowStyles?: PanelRowPresentationProps;
    panelContentStyles?: InjectableCss;
    parentBtnStyles?: InjectableCss;
    clearAllBtnStyles?: InjectableCss;
    applyBtnStyles?: InjectableCss;
    attributeTypeNameStyles?: InjectableCss;
    showResultsStyles?: InjectableCss;
    searchinputStyles?: TextFieldPresentationProps;
    drawerMainContainerStyles?: InjectableCss;
    seeLessLinkStylesMobile?: InjectableCss;
    seeAllLinkStylesMobile?: InjectableCss;
    searchinputStylesWrapper?: InjectableCss;
}

export const productListFilterAccordionSectionStyles: ProductListFilterAccordionSectionStyles = {
    accordionMainContainerStyles: {
        css: css`
            display: block;
            @media (min-width: 768px) and (max-width: 991px) {
                padding-left: 16px;
                padding-right: 16px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    accordionSection: {
        toggleIconProps: {
            src: NISUpIcon,
        },

        expandIconProps: {
            src: NISDownIcon,
        },
        headerProps: {
            css: css`
                button[aria-expanded="true"] {
                    border-bottom: 1px solid ${COLORS.disabled.dark} !important;
                    padding: 11px 9px !important;
                    padding-left: 15px !important;
                    background: #F5F6F7 !important;
                    border-radius:16px 16px 0 0;
                    margin-top: -1px;
                }
                button[aria-expanded="false"] {
                    padding: 11px 9px !important;
                    padding-left: 15px !important;
                }
                > button {
                    border: none !important;
                    border-bottom: 1px solid ${COLORS.disabled.dark} !important;
                    background: none !important;
                    &:focus, &:visited, &:active, &:hover {
                        padding: 11px 9px !important;
                        padding-left: 15px !important;
                    }
                    span {
                        color: ${getProp("theme.typography.p.color")} !important;
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        line-height: ${TYPOGRAPHY.lineHeight.base};
                        font-size: ${TYPOGRAPHY.size.base} !important;
                        font-family: "Roboto";
                        transition: none !important;
                        svg {
                            height: 16px;
                            width: 16px;
                        }
                    }
                }
            `,
        },
        panelProps: {
            css: css`
                overflow-y: auto;
                overflow-x: hidden;
                max-height: 350px;
                border: none;
                padding: 1px 15px;
                background: #F5F6F7;
                border-radius: 0px 0px 16px 16px;
                margin-bottom: 2px;
            `,
        },
    },
    input: {
        iconProps: { src: SearchIcon },
        cssOverrides: {
            formField: css`
                width: 100%;
                div {
                    input {
                        border-radius: 40px !important;
                        padding: 10px 16px !important;
                        padding-right: 32px !important;
                        height: 32px;
                        background-color: ${COLORS.background.grayShade} !important;
                        color: ${COLORS.black} !important;
                        font-size: ${TYPOGRAPHY.size.smaller} !important;
                        font-weight: ${TYPOGRAPHY.weight.base};
                    }
                    span {
                        top: 10px !important;
                        right: 12px !important;
                        width: 12px !important;
                        height: 12px !important;
                        padding: 0px !important;
                        svg {
                            width: 12px;
                            height: 12px;
                        }
                    }
                }
            `,
        },
    },
    checkBoxGroup: {
        css: css`
            width: 100%;
            display: grid !important;
            @media all and (max-width: 767px) {
                padding-left: 19px;
            }
            &:not(:last-child) {
                padding-bottom: 0;
                border-bottom: 1px solid ${COLORS.disabled.dark};
                @media all and (max-width: 767px) {
                    border-bottom: none;
                }               
            }
        `,
    },
    checkBox: {
        css: css<CheckboxStyleProps>`
            display: flex;
            justify-content: center;
            align-items: flex-start;
            flex-direction: row;
            span {
                border: 2px solid #9e9d9e !important;
                font-size: ${TYPOGRAPHY.size.smaller} !important;
                &:focus {
                    box-shadow: none !important;
                }
            }
            label {
                font-weight: ${TYPOGRAPHY.weight.base};
                font-size: ${TYPOGRAPHY.size.smaller};
                line-height: 20px;
                color: ${getProp("theme.typography.p.color")};
                width: 100%;
                margin: 0 !important;
                padding-left: 8px !important;
                > div {
                    display: flex;
                    gap: 4px;
                    div {
                        display: inline-block
                    }
                }
            }
            span[role="checkbox"] {
                height: 16px;
                width: 16px;
                border-radius: 2px !important;
                min-width: 16px !important;
                border: 2px solid #262525 !important;
                svg {
                    height: 16px;
                    width: 16px;
                }
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-word;
            `,
        },
    },
    checkBoxSelected: {
        css: css<CheckboxStyleProps>`
            display: flex;
            justify-content: center;
            align-items: flex-start;
            flex-direction: row;
            span {
                border: 2px solid ${COLORS.black} !important;
                font-size: ${TYPOGRAPHY.size.smaller} !important;
                &:focus {
                    box-shadow: none !important;
                }
            }
            label {
                font-weight: ${TYPOGRAPHY.weight.base};
                font-size: ${TYPOGRAPHY.size.smaller};
                line-height: 20px;
                color: ${getProp("theme.typography.p.color")};
                width: 100%;
                margin: 0 !important;
                word-break: break-word;
                padding-left: 8px !important;
                > div {
                    display: flex;
                    gap: 4px;
                    div {
                        display: inline-block
                    }
                }
            }
            span[role="checkbox"] {
                height: 16px;
                width: 16px;
                min-width: auto !important;
                border-radius: 2px !important;
                background: none !important;
                color: ${COLORS.black} !important;
                svg {
                    height: 16px;
                    width: 16px;
                }
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-word; ;
            `,
            // weight: "bold",
        },
    },
    seeAllLink: {
        css: css`
            padding-top: 5px;
        `,
    },
    seeLessLink: {
        css: css`
            padding-top: 5px;
        `,
    },

    // Drawer Section Styles
    seeLessLinkStylesMobile: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-left: 19px;
                        padding-top: 8px;
                    `,
                    css`
                        margin-left: 19px;
                        padding-top: 8px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    seeAllLinkStylesMobile: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-left: 19px;
                        padding-top: 8px;
                    `,
                    css`
                        margin-left: 19px;
                        padding-top: 8px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    searchinputStylesWrapper: {
        css: css`
            border-top: 1px solid #BCC3CD;
            width: 100%;
            margin-top: 16px;
            margin-bottom: 8px;
        `,
    },
    drawerMainContainerStyles: {
        css: css`
            display: none;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: block;
                    `,
                    css`
                        display: block;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    panelMenuStyles: {
        closeIconProps: {
            src: "X",
            css: css`
                margin-left: 50px;
            `,
        },

        headerColor: `#FFFFFF`,
        bodyColor: `#FFFFFF`,
        backIconProps: {
            src: NISArrowLeftIcon,
            css: css`
                margin-right: 60px;
                width: 18px;
                height: 11.25px;
                transform: translateX(-5px);
                svg {
                    width: 18px;
                    height: 11.25px;
                }
            `,
        },
    },
    panelrowStyles: {
        css: css`
            background: ${COLORS.white} !important;
            padding: 0px;
        `,
    },
    drawerStyles: {
        size: 350,
        cssOverrides: {
            drawerBody: css`
                background: ${COLORS.white} !important;
            `,
            drawerContent: css`
                nav {
                    border-bottom: 1px solid #bcc3cd;
                    padding: 20px 18px 20px 16px;
                }
            `,
            drawerTitle: css`
                border-bottom: 1px solid #bcc3cd;
            `,
            headlineTypography: css`
                font-size: 16px !important;
                font-weight: 600 !important;
                line-height: 32px !important;
                margin-left: 16px !important;
                margin-top: 15px !important;
            `,
        },
    },
    childrenStyles: {
        css: css`
            display: flex;
            margin-top: 32px;
            align-items: center;
        `,
    },
    checkboxStyles: {
        css: css`
            margin-right: 12px;
            > span[role="checkbox"] {
                border: 1px solid #262525;
                min-width: auto !important;
                border-radius: 2px !important;
                background: none !important;
                color: #262525 !important;
            }
        `,
        iconProps: {
            size: 24,
        },
        color: `#262525;`,
    },
    childrenPanelrowStyles: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            background: ${COLORS.white};
            padding: 0px 10px 0px 20px;
        `,
    },
    panelContentStyles: {
        css: css`
            font-size: 14px;
            font-weight: 600;
            line-height: 14px;
            color: #333333;
        `,
    },
    clearAllBtnStyles: {
        css: css`
            background: #636161;
            opacity: 0.5;
            border-radius: 16px;
            border: none;
            > span {
                font-size: 14px;
                font-weight: 600;
                line-height: 16px;
            }
        `,
    },
    applyBtnStyles: {
        css: css`
            border-radius: 16px;
        `,
    },
    parentBtnStyles: {
        css: css`
            border-top: 1px solid #bcc3cd !important;
            display: flex;
            justify-content: space-around;
            padding-top: 20px;
        `,
    },
    searchinputStyles: {
        iconProps: { src: SearchIcon },
        cssOverrides: {
            formField: css`
                margin-top: 16px !important;
                width: 90%;
                display: block;
                margin: auto;
            `,
            inputSelect: css`
                background: transparent !important;
                border-radius: 16px;
                padding-left: 16px !important;
                font-size: 14px;
                font-family: 'Roboto';
            `,
        },
    },
    showResultsStyles: {
        css: css`
            display: block;
            height: 32px;
            margin: 20px auto;
            border-radius: 16px;
        `,
    },
    attributeTypeNameStyles: {
        css: css`
            position: absolute;
            top: 7px;
            left: 44px;
            z-index: 1;
            color: #2B5197;
            font-size: 24px;
            font-weight: 600;
            line-height: 32px;
        `,
    },
};

export interface Props {
    title: string;
    facets: FacetModel[];
    onChangeFacet: (facet: FacetModel) => void;
    showMoreLimit: number;
    extendedStyles?: ProductListFilterAccordionSectionStyles;
    expandByDefault: boolean;
}

const NISProductListFiltersAccordionSection: FC<Props> = ({
    title,
    facets,
    onChangeFacet,
    showMoreLimit,
    expandByDefault,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeToNew(productListFilterAccordionSectionStyles, extendedStyles));
    const [expanded, setExpanded] = useState(false);
    const [filter, setFilter] = useState("");

    const limitedFacets = expanded ? facets : facets?.slice(0, showMoreLimit);
    const anySelected = facets.find(f => f.selected) !== undefined;


    const filterDrawerCloseHandler = () => {
        const btnElem = document.getElementById("show-filtered-results");
        if (btnElem) {
            btnElem.click();
        }
    };

    return (
        <>
            <StyledWrapper {...styles.accordionMainContainerStyles}>
                <ManagedAccordionSection
                    title={title}
                    {...styles.accordionSection}
                    initialExpanded={expandByDefault || anySelected}
                >
                    {limitedFacets
                        ?.filter(facet => facet.name?.toLowerCase().includes(filter.toLowerCase()) || filter === "")
                        .map(facet => (
                            <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                                <Checkbox
                                    {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                                    checked={facet.selected}
                                    onChange={() => onChangeFacet(facet)}
                                    data-test-selector={`facetFilter${facet.id}`}
                                >
                                    {facet.name} {facet.count !== -1 && `(${facet.count})`}
                                </Checkbox>
                            </CheckboxGroup>
                        ))}

                    {facets &&
                        facets.length > showMoreLimit &&
                        (expanded ? (
                            <Link {...styles.seeLessLink} onClick={() => setExpanded(false)}>
                                {translate("See Less")}
                            </Link>
                        ) : (
                            <Link {...styles.seeAllLink} onClick={() => setExpanded(true)}>
                                {translate("See All")}
                            </Link>
                        ))}
                </ManagedAccordionSection>
            </StyledWrapper>
            <StyledWrapper {...styles.drawerMainContainerStyles}>
                <PanelMenu
                    {...styles.panelMenuStyles}
                    layer={0}
                    closeOverlay={filterDrawerCloseHandler}
                    panelTrigger={
                        <PanelRow {...styles.panelrowStyles}>
                            <Typography {...styles.panelContentStyles}>{title}</Typography>
                            <ArrowRightIcon />
                        </PanelRow>
                    }
                >
                    <Typography {...styles.attributeTypeNameStyles}>{title}</Typography>
                    <StyledWrapper {...styles.searchinputStylesWrapper}>
                    <TextField
                        {...styles.searchinputStyles}
                        id="userName"
                        onChange={event => setFilter(event.target.value)}
                        value={filter}
                        placeholder={translate(`Search ${title}`)}
                    />
                    </StyledWrapper>
                    {limitedFacets?.filter(facet => facet.name?.toLowerCase().includes(filter.toLowerCase()) || filter === "")
                    .map(facet => (
                        <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                            <Checkbox
                                {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                                checked={facet.selected}
                                onChange={() => onChangeFacet(facet)}
                                data-test-selector={`facetFilter${facet.id}`}
                            >
                                {facet.name} {facet.count !== -1 && `(${facet.count})`}
                            </Checkbox>
                        </CheckboxGroup>
                    ))}
                    {facets &&
                        facets.length > showMoreLimit &&
                        (expanded ? (
                            <Link {...styles.seeLessLinkStylesMobile} onClick={() => setExpanded(false)}>
                                {translate("See Less")}
                            </Link>
                        ) : (
                            <Link {...styles.seeAllLinkStylesMobile} onClick={() => setExpanded(true)}>
                                {translate("See All")}
                            </Link>
                        ))}
                </PanelMenu>
            </StyledWrapper>
        </>
    );
};

export default NISProductListFiltersAccordionSection;
